import { useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "./client";
import { getActingTenantId, getRole } from "../auth/session";
import type { Device, ListResponse, SingleResponse } from "./types";

export interface DeviceStatusCounts {
  online: number;
  // Server-derived freshness bucket (lastSeen + checkInMinutes).
  // Optional so the dashboard renders cleanly against older API
  // builds that haven't shipped the count yet — absent → 0.
  stale?: number;
  offline: number;
  inactive: number;
  updating: number;
  error: number;
}

export interface TaskOverview {
  scheduled: number;
  queued: number;
  inProgress: number;
  paused: number;
}

export interface FirmwareBucket {
  version: string;
  count: number;
}

export interface DashboardResponse {
  deviceStatus: DeviceStatusCounts;
  taskOverview: TaskOverview;
  firmwareVersions: FirmwareBucket[];
  // Every live device, whatever its status. Absent on older API builds,
  // where the status counts add up to the total.
  deviceTotal?: number;
  // false when some figures are lower bounds — online, stale and offline
  // come from each device's last check-in, and a large fleet can't all be
  // checked on every load. Absent means exact.
  exact?: boolean;
}

export function useDashboard() {
  const role = getRole();
  const actingTenantId = getActingTenantId();
  const isPlatformDashboard = role === "super_admin" && !actingTenantId;
  const path = isPlatformDashboard ? "/api/v1/admin/dashboard" : "/api/v1/dashboard";

  return useQuery({
    queryKey: ["dashboard", isPlatformDashboard ? "platform" : actingTenantId ?? "tenant"],
    queryFn: async () => {
      try {
        return await apiFetch<SingleResponse<DashboardResponse>>(path);
      } catch (err) {
        if (isPlatformDashboard && err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          return dashboardFromAdminDevices();
        }
        throw err;
      }
    },
  });
}

// Fallback for API builds without /admin/dashboard: ask the device list for
// a count per status rather than walking every device in the browser.
async function dashboardFromAdminDevices(): Promise<SingleResponse<DashboardResponse>> {
  const statuses = ["online", "stale", "offline"] as const;
  const totals = await Promise.all(
    statuses.map(async (status) => {
      const qs = new URLSearchParams({ status, countOnly: "true" });
      const res = await apiFetch<ListResponse<Device & { tenantId: string }>>(`/api/v1/admin/devices?${qs.toString()}`);
      return res.pagination.total ?? 0;
    }),
  );
  const [online, stale, offline] = totals;

  return {
    data: {
      deviceStatus: { online, stale, offline, inactive: 0, updating: 0, error: 0 },
      taskOverview: { scheduled: 0, queued: 0, inProgress: 0, paused: 0 },
      firmwareVersions: [],
    },
  };
}
